/**
 * 動的CSS読み込みユーティリティ
 */

const PALETTE_CSS_ID = 'dynamic-color-palette';
const CSS_CACHE = new Map();

/**
 * カラーパレット用のCSSを動的に読み込み・適用する
 * @param {string} paletteKey - パレットキー
 * @param {Object} customPalettes - カスタムパレットデータ
 * @param {string} baseUrl - ベースURL（デフォルト: 現在のオリジン）
 */
export async function loadPaletteCSS(paletteKey, customPalettes = {}, baseUrl = null) {
    try {
        // 管理画面の場合はスキップ
        if (isAdminDomain()) {
            return;
        }

        // ベースURLを決定
        const origin = baseUrl || window.location.origin;

        // URLパラメータを構築
        const params = new URLSearchParams({
            palette: paletteKey || 'default'
        });

        // カスタムパレットがある場合は追加
        if (Object.keys(customPalettes).length > 0) {
            params.append('customPalettes', encodeURIComponent(JSON.stringify(customPalettes)));
        }

        const cssUrl = `${origin}/api/generate-palette-css?${params.toString()}`;

        // キャッシュチェック
        const cacheKey = `${paletteKey}-${JSON.stringify(customPalettes)}`;
        if (CSS_CACHE.has(cacheKey)) {
            applyCachedCSS(CSS_CACHE.get(cacheKey));
            return;
        }

        // CSS取得
        const response = await fetch(cssUrl);
        if (!response.ok) {
            throw new Error(`CSS取得失敗: ${response.status}`);
        }

        const cssText = await response.text();

        // キャッシュに保存
        CSS_CACHE.set(cacheKey, cssText);

        // CSS適用
        applyCachedCSS(cssText);


    } catch (error) {
        console.warn('⚠️ カラーパレットの読み込みに失敗:', error);

        // フォールバック: デフォルトパレットを適用
        if (paletteKey !== 'default') {
            await loadPaletteCSS('default', {}, baseUrl);
        }
    }
}

/**
 * キャッシュされたCSSを適用
 * @param {string} cssText - CSS文字列
 */
function applyCachedCSS(cssText) {
    // 既存のパレットCSSを削除
    const existingLink = document.getElementById(PALETTE_CSS_ID);
    if (existingLink) {
        existingLink.remove();
    }

    // 新しいstyleタグを作成
    const styleElement = document.createElement('style');
    styleElement.id = PALETTE_CSS_ID;
    styleElement.textContent = cssText;

    // headに追加
    document.head.appendChild(styleElement);
}

/**
 * 管理画面かどうかを判定
 * @returns {boolean}
 */
function isAdminDomain() {
    const hostname = window.location.hostname;

    // ローカル開発環境の場合
    if (hostname.includes('localhost') || hostname === '127.0.0.1') {
        return true;
    }

    // suisui.life（メインドメイン）の場合は管理画面
    if (hostname === 'suisui.life') {
        return true;
    }

    // サブドメインの場合は公開ページ
    const parts = hostname.split('.');
    if (parts.length > 2 &&
        parts[parts.length - 2] === 'suisui' &&
        parts[parts.length - 1] === 'life') {
        return false;
    }

    // その他の場合は管理画面として扱う
    return true;
}

/**
 * CSSキャッシュをクリア
 */
export function clearPaletteCSSCache() {
    CSS_CACHE.clear();
    console.log('🗑️ カラーパレットCSSキャッシュをクリア');
}

/**
 * 現在適用されているパレットを取得
 * @returns {string|null} パレットキー
 */
export function getCurrentPalette() {
    const bodyStyle = window.getComputedStyle(document.body, '::before');
    const content = bodyStyle.getPropertyValue('content');

    if (content && content.startsWith('"palette-') && content.endsWith('"')) {
        return content.slice(9, -1); // "palette-" と末尾の " を除去
    }

    return null;
}

/**
 * パレットが変更されたかチェック
 * @param {string} expectedPalette - 期待されるパレット
 * @returns {boolean}
 */
export function isPaletteChanged(expectedPalette) {
    const currentPalette = getCurrentPalette();
    return currentPalette !== expectedPalette;
}

